import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useOrganization } from "@/hooks/useOrganization";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Search, Users, Trophy, Heart } from "lucide-react";
import LevelBadge from "@/components/level/LevelBadge";
import RankingList from "@/components/level/RankingList";
import FriendsList from "@/components/social/FriendsList";

// UPPDRAG-58: tab values are stable URL slugs (bookmarkable). Order
// matters for picking the default tab when the requested one is hidden.
const TAB_RANKING = "ranking" as const;
const TAB_SEARCH = "search" as const;
const TAB_FRIENDS = "friends" as const;
type PlayersTab = typeof TAB_RANKING | typeof TAB_SEARCH | typeof TAB_FRIENDS;
const DEFAULT_TAB_WITH_RANKING: PlayersTab = TAB_RANKING;
const DEFAULT_TAB_WITHOUT_RANKING: PlayersTab = TAB_SEARCH;

export default function Players() {
  const { t } = useTranslation("common");
  const { user } = useAuth();
  const { organization } = useOrganization();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [levelRange, setLevelRange] = useState([1, 10]);
  const [playerType, setPlayerType] = useState("all");
  const [hand, setHand] = useState("all");

  // UPPDRAG-58: read the per-org sub-flag. Defensive default-on:
  // `flags?.players_ranking_enabled !== false` treats null/undefined
  // (missing row OR loading) as enabled, matching the migration
  // DEFAULT and the SettingsFeatures sub-toggle's display logic.
  // useFeatureFlags' canonical isEnabled() helper closes by default,
  // which would briefly hide the tab during the first feature-flags
  // fetch — avoid that flicker by reading the raw flags object.
  const { flags } = useFeatureFlags();
  const rankingEnabled = (flags as Record<string, unknown> | null | undefined)?.players_ranking_enabled !== false;

  // UPPDRAG-58: bookmarkable tabs via ?tab=… URL syncing.
  //   - On mount + on flag change: read ?tab=, validate, fall back to
  //     the right default when ranking is hidden, redirect ?tab=ranking
  //     silently (no toast) when the flag is off.
  //   - On user click: setSearchParams keeps URL in lockstep with the
  //     visible tab (replace: true so the back button isn't polluted).
  const [searchParams, setSearchParams] = useSearchParams();
  const allowedTabs = useMemo<PlayersTab[]>(
    () => (rankingEnabled ? [TAB_RANKING, TAB_SEARCH, TAB_FRIENDS] : [TAB_SEARCH, TAB_FRIENDS]),
    [rankingEnabled],
  );
  const defaultTab = rankingEnabled ? DEFAULT_TAB_WITH_RANKING : DEFAULT_TAB_WITHOUT_RANKING;
  const requestedTab = searchParams.get("tab");
  const activeTab: PlayersTab = (allowedTabs as string[]).includes(requestedTab ?? "")
    ? (requestedTab as PlayersTab)
    : defaultTab;

  // Effect: if the URL says ?tab=ranking but the flag is off, rewrite
  // the URL to ?tab=search so refresh + share-link land in a sane
  // place. Silent (no toast) per spec.
  useEffect(() => {
    if (rankingEnabled) return;
    if (requestedTab !== TAB_RANKING) return;
    const next = new URLSearchParams(searchParams);
    next.set("tab", TAB_SEARCH);
    setSearchParams(next, { replace: true });
    // searchParams + setSearchParams are stable from react-router; only
    // re-run when the flag flips or the requested tab changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rankingEnabled, requestedTab]);

  const handleTabChange = (value: string) => {
    const next = new URLSearchParams(searchParams);
    next.set("tab", value);
    setSearchParams(next, { replace: true });
  };

  // Recently played with
  const { data: recentPartners = [] } = useQuery({
    queryKey: ["recent-partners", user?.id, organization?.id],
    queryFn: async () => {
      const { data: results } = await supabase
        .from("match_results")
        .select("winner_team, loser_team")
        .eq("organization_id", organization!.id)
        .or(`winner_team.cs.{${user!.id}},loser_team.cs.{${user!.id}}`)
        .order("played_at", { ascending: false })
        .limit(20);

      const partnerIds = new Set<string>();
      for (const r of results ?? []) {
        const allPlayers = [...(r.winner_team as string[]), ...(r.loser_team as string[])];
        allPlayers.forEach((pid) => { if (pid !== user!.id) partnerIds.add(pid); });
      }

      const ids = Array.from(partnerIds).slice(0, 6);
      if (ids.length === 0) return [];

      const { data: profiles } = await supabase
        .from("public_profiles" as any)
        .select("id, full_name, level, avatar_url, player_type")
        .in("id", ids);

      return profiles ?? [];
    },
    enabled: !!user?.id && !!organization?.id,
  });

  const { data: players = [], isLoading } = useQuery({
    queryKey: ["players-search", searchTerm, levelRange, playerType, hand],
    queryFn: async () => {
      // View enforces profile_visibility at row level — drop INTERNAL columns + .neq filter
      let q = supabase
        .from("public_profiles" as any)
        .select("id, full_name, level, player_type, preferred_hand, avatar_url")
        .gte("level", levelRange[0])
        .lte("level", levelRange[1])
        .order("level", { ascending: false })
        .limit(50);

      if (searchTerm.length >= 2) {
        q = q.ilike("full_name", `%${searchTerm}%`);
      }
      if (playerType !== "all") {
        q = q.eq("player_type", playerType);
      }
      if (hand !== "all") {
        q = q.eq("preferred_hand", hand);
      }
      if (user?.id) {
        q = q.neq("id", user.id);
      }

      const { data, error } = await q;
      if (error) throw error;
      return data ?? [];
    },
    enabled: true,
  });

  return (
    <div className="space-y-4 pb-20">
      <div className="gradient-hero rounded-2xl p-5 -mx-1">
        <h1 className="text-2xl font-bold flex items-center gap-2"><Users className="h-6 w-6" /> {t("players.title")}</h1>
        <p className="text-sm text-muted-foreground">{t("players.subtitle")}</p>
      </div>

      {/* UPPDRAG-58: Tabs is now controlled (value + onValueChange)
          so URL ?tab= stays in sync. The Ranking trigger + content
          are conditionally rendered based on the per-org flag. */}
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="w-full">
          {rankingEnabled && (
            <TabsTrigger value={TAB_RANKING} className="flex-1 gap-1"><Trophy className="h-3.5 w-3.5" /> {t("players.tabs.ranking")}</TabsTrigger>
          )}
          <TabsTrigger value={TAB_SEARCH} className="flex-1 gap-1"><Search className="h-3.5 w-3.5" /> {t("players.tabs.search")}</TabsTrigger>
          <TabsTrigger value={TAB_FRIENDS} className="flex-1 gap-1"><Heart className="h-3.5 w-3.5" /> {t("players.tabs.friends")}</TabsTrigger>
        </TabsList>

        {rankingEnabled && (
          <TabsContent value={TAB_RANKING} className="mt-3">
            <RankingList />
          </TabsContent>
        )}

        <TabsContent value={TAB_SEARCH} className="mt-3 space-y-4">
          {/* Recently played with */}
          {recentPartners.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-muted-foreground">{t("players.recentlyPlayedWith")}</h3>
              <div className="flex gap-3 overflow-x-auto pb-1">
                {recentPartners.map((p: any) => (
                  <button
                    key={p.id}
                    onClick={() => navigate(`/players/${p.id}`)}
                    className="flex flex-col items-center gap-1 shrink-0 group"
                  >
                    <div className="h-12 w-12 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center ring-2 ring-border group-hover:ring-primary transition-colors">
                      <span className="text-sm font-bold">
                        {(p.full_name?.split(" ")[0]?.[0] || "?").toUpperCase()}
                      </span>
                    </div>
                    <span className="text-[11px] text-muted-foreground truncate max-w-[4.5rem]">
                      {p.full_name?.split(" ")[0]}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t("players.searchNamePlaceholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Filters */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">{t("players.playStyle")}</Label>
              <Select value={playerType} onValueChange={setPlayerType}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("all")}</SelectItem>
                  <SelectItem value="offensive">{t("players.playerType.offensive")}</SelectItem>
                  <SelectItem value="defensive">{t("players.playerType.defensive")}</SelectItem>
                  <SelectItem value="allround">{t("players.playerType.allround")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs text-muted-foreground">{t("players.hand.label")}</Label>
              <Select value={hand} onValueChange={setHand}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("all")}</SelectItem>
                  <SelectItem value="right">{t("players.hand.right")}</SelectItem>
                  <SelectItem value="left">{t("players.hand.left")}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">{t("players.levelRange", { min: levelRange[0], max: levelRange[1] })}</Label>
            <Slider min={1} max={10} step={1} value={levelRange} onValueChange={setLevelRange} />
          </div>

          {/* Results */}
          {isLoading ? (
            <div className="space-y-2">{[1,2,3].map(i => <Skeleton key={i} className="h-16" />)}</div>
          ) : players.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">{t("players.noPlayersFound")}</p>
          ) : (
            <div className="space-y-2">
              {players.map((p: any) => (
                <Card key={p.id} className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => navigate(`/players/${p.id}`)}>
                  <CardContent className="p-3 flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-gradient-to-br from-primary/20 to-accent/20 flex items-center justify-center shrink-0">
                      <span className="text-sm font-bold text-foreground">
                        {(p.full_name?.split(" ")[0]?.[0] || "?").toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{p.full_name || t("unknown")}</p>
                      <div className="flex items-center gap-2 text-xs text-muted-foreground">
                        {p.player_type && <span>{t(`players.playerType.${p.player_type}`)}</span>}
                        {p.preferred_hand && <span>· {t(`players.hand.${p.preferred_hand}`)}</span>}
                      </div>
                    </div>
                    {p.level !== null && p.level !== undefined && <LevelBadge level={p.level} size="sm" />}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value={TAB_FRIENDS} className="mt-3">
          <FriendsList />
        </TabsContent>
      </Tabs>
    </div>
  );
}